import { Logger } from '@streem/logger';
import { DetailSession, Recording, Streemshot } from '@streem/sdk-core';
import { styled } from '@streem/ui-react';
import { RefObject, useEffect } from 'react';
import { WallItem } from 'streem-sdk-protobuf';
import { isRecordingReady } from '../../util/artifacts';
import { StreemshotImageData } from '../../util/media_gallery_selected_utils';
import { MediaGalleryHeader } from './shared.styles';
import { StreemshotThumbnail } from './thumbnail';
import { useTranscriptions } from '../../hooks/use_transcriptions';

interface StreemshotsProps {
    streemshots: Streemshot[];
    recordings: Recording[];
    streemshotsImageDataRef: RefObject<StreemshotImageData>;
    selectedStreemshotIds: Set<string>;
    handleArtifactSelect: (artifactType: WallItem.ArtifactType, artifactSid: string) => void;
    selectionVisible: boolean;
    log: Logger;
    detailSession: DetailSession;
}
export function Streemshots({
    streemshots,
    recordings,
    streemshotsImageDataRef,
    selectedStreemshotIds,
    handleArtifactSelect,
    selectionVisible,
    log,
    detailSession,
}: StreemshotsProps): JSX.Element {
    const someRecordingsReady = isRecordingReady(recordings);

    useEffect(() => {
        log.info(`Media gallery is rendering ${streemshots.length} streemshots`);
    }, [log, streemshots.length]);

    useEffect(() => {
        const streemshotImageDataCache = streemshotsImageDataRef.current;
        return function dataUrlCleanup() {
            // Clean url's data from browser
            const keys = Object.keys(streemshotImageDataCache);
            keys.forEach(key => {
                const { dataURL } = streemshotImageDataCache[key];
                URL.revokeObjectURL(dataURL);
            });
        };
    }, [streemshotsImageDataRef]);

    const transcriptions = useTranscriptions(detailSession);

    return (
        <StreemshotSection>
            <MediaGalleryHeader>Photos</MediaGalleryHeader>
            <StreemshotsGrid>
                {streemshots.map(streemshot => {
                    return (
                        <StreemshotThumbnail
                            onClickTimeStamp={(timestamp, timestampController) => {
                                timestampController.jumpVideoToTimeStamp(timestamp);
                                transcriptions.updateAutoScrollMode(true);
                            }}
                            canClickTimeStamp={someRecordingsReady && recordings.length > 0}
                            canSelect={true}
                            imageDataRef={streemshotsImageDataRef}
                            isSelected={
                                selectedStreemshotIds.has(streemshot.id) ||
                                selectedStreemshotIds.has(streemshot?.streemshotId)
                            }
                            isSelectVisible={selectionVisible}
                            key={streemshot.id}
                            onSelect={() =>
                                handleArtifactSelect(
                                    WallItem.ArtifactType.STREEMSHOT,
                                    streemshot?.streemshotId || streemshot.id,
                                )
                            }
                            streemshot={streemshot}
                        />
                    );
                })}
            </StreemshotsGrid>
        </StreemshotSection>
    );
}

const StreemshotSection = styled('section')({
    gridColumn: 'span 2',
    '@media(max-width: 1075px)': {
        gridColumn: 'span 1',
    },
});

const StreemshotsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(15.625rem, 1fr))',
    gridAutoRows: '25rem',
    gridAutoFlow: 'dense',
    gridGap: '1rem',
    marginTop: '1rem',
});
